import {
  Activity,
  BarChart3,
  Container,
  CreditCard,
  FileText,
  Home,
  type LucideIcon,
  Mail,
  Mic,
  ServerCog,
  Users,
  Users2,
} from "lucide-react";
import type { SystemRole } from "@/lib/role-hooks";

export type NavigationCaption = string | Partial<Record<SystemRole, string>>;
export type NavigationLabel = string | Partial<Record<SystemRole, string>>;

export type NavigationSubItem = {
  to: string;
  label: NavigationLabel;
  caption?: NavigationCaption;
  end?: boolean;
};

export type NavigationItem = {
  to: string;
  label: NavigationLabel;
  shortLabel: string;
  caption: NavigationCaption;
  icon: LucideIcon;
  image?: string;
  roles: readonly SystemRole[];
  subItems?: readonly NavigationSubItem[];
};

export const NAVIGATION_ITEMS: readonly NavigationItem[] = [
  {
    to: "/dashboard",
    label: "Dashboard",
    shortLabel: "Dashboard",
    caption: "A quick overview of your sites",
    icon: Home,
    roles: ["site_manager"],
  },
  {
    to: "/sites",
    label: "Sites",
    shortLabel: "Sites",
    caption: "View all sites and export activity logs",
    icon: Container,
    image: "/icons/quick-actions/sites.png",
    roles: ["site_manager"],
  },
  {
    to: "/workers",
    label: "Workers",
    shortLabel: "Workers",
    caption: "View all workers and view activity logs",
    icon: Users,
    image: "/icons/quick-actions/workers.png",
    roles: ["site_manager"],
  },
  {
    to: "/forms",
    label: "Forms",
    shortLabel: "Forms",
    caption: "Permits, training, records and templates",
    icon: FileText,
    image: "/icons/quick-actions/forms.png",
    roles: ["site_manager"],
    subItems: [
      {
        to: "/forms/permits",
        label: "Permits",
        caption: "View submitted work permit requests",
        end: true,
      },
      {
        to: "/forms/training",
        label: "Training",
        caption: "Site inductions, toolbox talks, and training records",
        end: true,
      },
      {
        to: "/forms/records",
        label: "Records",
        caption: "Inspection reports, risk assessments, and incident records",
        end: true,
      },
      {
        to: "/forms/templates",
        label: "Templates",
        caption: "Manage form templates",
        end: false,
      },
    ],
  },
  {
    to: "/reports",
    label: "Reports",
    shortLabel: "Reports",
    caption: "Filter activity and export data",
    icon: BarChart3,
    image: "/icons/quick-actions/reports.png",
    roles: ["site_manager"],
  },
  {
    to: "/email",
    label: "Email",
    shortLabel: "Email",
    caption: "Documents and messages emailed into your sites",
    icon: Mail,
    roles: ["site_manager"],
  },
  {
    to: "/notes",
    label: "Notes",
    shortLabel: "Notes",
    caption: "Toolbox talks and voice recordings",
    icon: Mic,
    roles: ["site_manager"],
  },
] as const satisfies readonly NavigationItem[];

export const MANAGER_QUICK_ACTION_ITEMS = NAVIGATION_ITEMS.filter(
  (item) => item.roles.includes("site_manager") && item.to !== "/dashboard"
);

export type NavigationSection = {
  label?: string;
  items: readonly NavigationItem[];
};

export const ADMIN_NAVIGATION_SECTIONS: readonly NavigationSection[] = [
  {
    label: "Administration",
    items: [
      {
        to: "/admin/sites",
        label: "Sites overview",
        shortLabel: "Sites",
        caption: "Review all sites and recent activity",
        icon: Container,
        roles: ["admin"],
      },
      {
        to: "/admin/users",
        label: "User management",
        shortLabel: "Users",
        caption: "Review accounts and roles",
        icon: Users2,
        roles: ["admin"],
      },
      {
        to: "/admin/system-health",
        label: "System health",
        shortLabel: "Health",
        caption: "Review API health and operational signals",
        icon: ServerCog,
        roles: ["admin"],
      },
      {
        to: "/admin/billing-oversight",
        label: "Billing oversight",
        shortLabel: "Billing",
        caption: "Track subscriptions and credit usage",
        icon: CreditCard,
        roles: ["admin"],
      },
      {
        to: "/admin/audit-log",
        label: "Audit log",
        shortLabel: "Audit",
        caption: "Inspect high-impact changes",
        icon: Activity,
        roles: ["admin"],
      },
      {
        to: "/admin/unresolved-email",
        label: "Unresolved email",
        shortLabel: "Email",
        caption: "Triage inbound email with no matching site",
        icon: Mail,
        roles: ["admin"],
      },
    ],
  },
];

export function getNavigationSectionsForRole(
  systemRole: SystemRole
): readonly NavigationSection[] {
  if (systemRole === "admin") {
    return ADMIN_NAVIGATION_SECTIONS;
  }

  return [
    {
      items: NAVIGATION_ITEMS.filter((item) => item.roles.includes(systemRole)),
    },
  ] satisfies readonly NavigationSection[];
}
